import { useEffect } from 'react';
import Select from "react-select";
import { Modal } from "react-bootstrap";
import {
  ICustomInputRequestModels,
  ITypeCustomInput,
  ITypeCustomInputPayload,
  IvrCustomField,
} from "../../../models";
import { useForm } from "react-hook-form";
import {
  updateCustomInputFields,
  useFunctionalityDepartment,
  useFunctionalityLocation,
} from "../../../pages/hooks";
import { ExtendedCriteriaType, InputFieldType } from "../../../configs";
import { FormErrorMessage } from "../../../components";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  header?: string;
  ivrId?: string;
  customFields?: IvrCustomField[];
  onSaveHandler?: any;
  callId?: string;
  callerId?: string;
  callerCompanyId?: string;
  getCustomInputData?: ITypeCustomInput[];
  initialValues?: Record<string, any>;
  onSubmitted?: (values: Record<string, any>) => void;
};

export function ModalIvrCustomField(props: Props) {
  const {
    show,
    handleShow,
    callId,
    callerId,
    callerCompanyId,
    getCustomInputData,
    initialValues,
    onSubmitted,
  } = props;

  const { optionMappedLocationList } = useFunctionalityLocation({
    companyId: callerCompanyId!, shouldLocationGetAllInvoke: show
  });
  const { optionMappedDepartmentList } = useFunctionalityDepartment({
    companyId: callerCompanyId!, shouldDepartmentGetAllInvoke: show
  });

  const updatedOptionMappedLocationList = [
    { value: "", label: "Any" },
    ...optionMappedLocationList,
  ];
  const updatedOptionMappedDepartmentList = [
    { value: "", label: "Any" },
    ...optionMappedDepartmentList,
  ];

  const { register, handleSubmit, formState: { errors }, setValue, reset, watch } = useForm<any>({
    defaultValues: initialValues ?? {},
  });
  const getFieldKey = (name: string) => name.replace(/[^a-zA-Z0-9 ]/g, "");

  const onUpdateCustomInputFieldsHandler = async (form: any) => {
    let customInputRequestModels: ICustomInputRequestModels[] = [];
    getCustomInputData?.forEach((customInput: ITypeCustomInput) => {
      if (form[getFieldKey(customInput.name)]) {
        customInputRequestModels.push({
          name: customInput.name,
          value: form[getFieldKey(customInput.name)],
          isEnabled: true,
          criteriaType: customInput.criteriaType,
        });
      }
    });

    const payload: ITypeCustomInputPayload | undefined =
      customInputRequestModels.length
        ? { customInputRequestModels: customInputRequestModels }
        : undefined;
    const didSave = await updateCustomInputFields(callId, payload, handleShow);
    if (didSave && onSubmitted) {
      onSubmitted(form);
    }
  };

  useEffect(() => {
    if (show) {
      reset(initialValues ?? {});
    }
  }, [show, reset, initialValues]);

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      centered
      backdrop="static"
    >
      <form onSubmit={handleSubmit(onUpdateCustomInputFieldsHandler)}>
        <Modal.Header closeButton>
          <Modal.Title>Custom Inputs</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="px-3 py-2">
            <div className="row mt-1">
              <div className="col-12">
                {getCustomInputData ? (
                  getCustomInputData.length ? (
                    getCustomInputData?.map(
                      (customInput: ITypeCustomInput, index) => {
                        const fieldKey = getFieldKey(customInput.name);
                        const selectedValue = watch(fieldKey);
                        const options =
                          customInput.criteriaType ===
                            ExtendedCriteriaType.Department
                            ? updatedOptionMappedDepartmentList
                            : customInput.criteriaType ===
                              ExtendedCriteriaType.Location
                              ? updatedOptionMappedLocationList
                              : [];
                        const selectedOption = options.find(
                          (option) => option.label === selectedValue
                        ) ?? null;
                        return (
                        <div key={index}>
                          <div className="row mb-2">
                            <div className="col-4">
                              <label
                                htmlFor=""
                                className={`text-muted font-sz-12 ${customInput.isMandatory ? "required" : ""
                                  }`}
                              >
                                {customInput.name}
                              </label>
                            </div>
                            <div className="col-8">
                              {customInput.inputType ===
                                InputFieldType.DropDown ? (
                                <>
                                  <input
                                    className="d-none"
                                    {...register(fieldKey, {
                                      required: customInput.isMandatory,
                                    })} />
                                  <Select
                                    className={` ${errors[fieldKey]
                                      ? "field-required"
                                      : ""
                                      } `}
                                    components={{
                                      IndicatorSeparator: () => null,
                                    }}
                                    options={options}
                                    placeholder={`Select a ${customInput.name}`}
                                    value={selectedOption}
                                    onChange={(option) => {
                                      setValue(
                                        fieldKey,
                                        option?.label ?? "",
                                        { shouldValidate: true }
                                      );
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    className={`form-control ${errors[fieldKey]
                                      ? "field-required"
                                      : ""
                                      }`}
                                    {...register(fieldKey, {
                                      required: customInput.isMandatory,
                                    })}
                                    onBlur={(event) => {
                                      setValue(
                                        fieldKey,
                                        event.target.value,
                                        { shouldValidate: true }
                                      );
                                    }} />
                                </>
                              )}
                              <span>
                                {errors[fieldKey] && (
                                  <FormErrorMessage message="This field is required" />
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    )
                  ) : (
                    <div className="text-center my-4">
                      Have no custom inputs!
                    </div>
                  )
                ) : (
                  <div className="text-center my-4">Loading...</div>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={() => handleShow(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase btn-message-submit custom-field-submit-btn-save"
          >
            Save
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
